import { Component, effect, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth-service/auth.service';
import { RegistrationService } from '../../services/registration.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { Router } from '@angular/router';
import { HealthCheckService } from 'src/app/shared/service/health-check.service';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent {
  profilForm = new FormGroup({
    email: new FormControl<string>('', Validators.required),
    password: new FormControl<string>('', Validators.required),
  });

  isPasswordVisible: boolean = false;

  authService = inject(AuthService);
  registrationService = inject(RegistrationService);
  currentUserService = inject(CurrentUserService);
  notificationService = inject(NotificationService);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  loading = false;
  healthCheckService = inject(HealthCheckService);

  constructor() {
    effect(() => {
      if (this.healthCheckService.backendStatus() == 'down') {
        this.serverDownMessage();
      }
    });
  }
  tooggleShowPassword(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  async serverDownMessage() {
    const message = await firstValueFrom(
      this.translocoService.selectTranslate(
        'calculator.error.serverUnavailable',
      ),
    );
    this.notificationService.error(message);
  }
  onLogin(): void {
    const email = this.profilForm.get('email')?.value;
    const password = this.profilForm.get('password')?.value;

    if (email && password) {
      this.loading = true;
      this.authService.login(email, password).subscribe({
        next: (response) => {
          // Handle language preference from login response
          if (response?.user?.preferredLanguage) {
            this.translocoService.setActiveLang(
              response.user.preferredLanguage,
            );
            localStorage.setItem(
              'selectedLanguage',
              response.user.preferredLanguage,
            );
          }

          // Refresh user data to ensure we get the correct user info
          this.currentUserService.refreshCurrentUser();

          this.router.navigate(['/']);
          this.loading = false;
        },
        error: (error) => {
          console.error('Login failed:', error);
          this.loading = false;

          // Check for email not verified error
          if (error.error?.code === 'E_EMAIL_NOT_VERIFIED') {
            // Track email not verified attempts for monitoring email delivery issues
            Sentry.addBreadcrumb({
              message: 'Login attempt with unverified email',
              level: 'warning',
              data: {
                emailDomain: email!.split('@')[1],
                errorCode: 'E_EMAIL_NOT_VERIFIED',
              },
            });
            this.handleEmailNotVerified(email!);
            return;
          }

          // Check for account pending approval error
          if (error.error?.code === 'E_ACCOUNT_PENDING_APPROVAL') {
            this.notificationService.warn(
              this.translocoService.translate('auth.accountPendingApproval'),
            );
            return;
          }

          // Check for account suspended error
          if (error.error?.code === 'E_ACCOUNT_SUSPENDED') {
            this.notificationService.error(
              this.translocoService.translate('auth.accountSuspended'),
            );
            return;
          }
          if (error.error?.code === 'E_COMPANY_DELETED') {
            this.notificationService.error(
              this.translocoService.translate('auth.companyDeleted'),
            );
            return;
          }
          // General error handling
          this.notificationService.error(
            this.translocoService.translate('calculator.error.loginFailed'),
          );
        },
      });
    } else {
      this.notificationService.warn(
        this.translocoService.translate('calculator.error.missingPwdOrEmail'),
      );
    }
  }

  private handleEmailNotVerified(email: string): void {
    // Store the email for the verification flow
    this.registrationService.storePendingVerificationEmail(email);

    // Show informative message
    this.notificationService.warn(
      'Email Verification Required - Please verify your email address before logging in. You will be redirected to resend a verification email.',
    );

    // Redirect to verification pending page after a short delay
    setTimeout(() => {
      this.router.navigate(['/auth/email-verification-pending']);
    }, 2000);
  }
}
